import Pusher, {
  Channel,
  ChannelAuthorizationCallback,
  ChannelAuthorizationHandler,
} from 'pusher-js';

export const PUSHER_ENV: string = process.env.REACT_APP_PUSHER_ENV ?? '';

const wrappedInstances = new WeakSet<Pusher>();
const loggedChannels = new WeakSet<Channel>();

export function createChannelAuthorizationHandler(
  getToken: () => Promise<string>,
  apiUrl: string,
): ChannelAuthorizationHandler {
  return (params, callback) => {
    let callbackCalled = false;
    const complete: ChannelAuthorizationCallback = (error, data) => {
      if (callbackCalled) return;
      callbackCalled = true;
      try {
        callback(error, data);
      } catch {
        return;
      }
    };

    void (async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${apiUrl}/pusher/auth`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            socket_id: params.socketId,
            channel_name: params.channelName,
          }).toString(),
        });

        if (!response.ok) {
          complete(
            new Error(
              `Pusher auth failed for ${params.channelName}: HTTP ${response.status}`,
            ),
            null,
          );
          return;
        }

        complete(null, await response.json());
      } catch (error) {
        complete(
          error instanceof Error ? error : new Error(String(error)),
          null,
        );
      }
    })();
  };
}

export function logSubscriptionErrors(instance: Pusher): void {
  if (wrappedInstances.has(instance)) return;
  wrappedInstances.add(instance);

  const subscribe = instance.subscribe.bind(instance);
  instance.subscribe = (channelName) => {
    const channel = subscribe(channelName);
    if (!loggedChannels.has(channel)) {
      loggedChannels.add(channel);
      channel.bind('pusher:subscription_error', (error: unknown) => {
        console.error(`[Pusher] subscription_error on ${channel.name}`, error);
      });
    }
    return channel;
  };
}
